/**
 * Typed route builder for the Phoenix-Portal app (mirrors the main portal's `APP_ROUTES`
 * convention — CLAUDE.md "Frontend-Routing": no literal route arrays in navigation, so a
 * typo becomes a compile error and a rename is single-file). Each entry returns the
 * `router.navigate(...)` commands array.
 *
 * The Backup Environments redesign (1:1 agent↔config): the agents surface IS the backup-environments
 * home, and the config editor reparents under each agent. There is NO standalone config
 * creation any more, and NO manual Backup Environment creation either (Kevin 2026-08-06): a
 * Backup Environment is provided through the Phoenix Site order / setup flow, so the old `configs` /
 * `configNew` / `environmentNew` entries are retired. The config-edit and observed/drift
 * views survive, reparented under the environment.
 *
 * AB#2499 (W1a): one environment is a routed WORKSPACE — a persistent shell plus four
 * addressable sections ({@link ENVIRONMENT_SECTIONS}). Every section is its own reload-safe URL,
 * so a tab is a real address (deep link, F5, back/forward), not a component-local flag.
 */

/**
 * The four sections of one environment workspace, in tab order. The literals ARE the URL
 * segments under `/backup-environments/{agentId}` and the `data.section` values of the child routes —
 * one vocabulary for routing, the tab bar and the shell's "which section is active" state.
 */
export const ENVIRONMENT_SECTIONS = ['overview', 'export', 'recovery', 'configuration'] as const;

/** One addressable section of the environment workspace. */
export type EnvironmentSection = (typeof ENVIRONMENT_SECTIONS)[number];

export const APP_ROUTES = {
  /** / — the public landing page (also the back-target of the public legal pages). */
  home: (): unknown[] => ['/'],
  /** /impressum — the public imprint ("Impressum") page (GDPR/TMG reachability). */
  impressum: (): unknown[] => ['/impressum'],
  /** /privacy-policy — the public privacy-policy page (GDPR Art. 12–14 reachability). */
  privacyPolicy: (): unknown[] => ['/privacy-policy'],
  /** /terms — the public terms-and-conditions (AGB) page. */
  terms: (): unknown[] => ['/terms'],
  /**
   * /onboarding — the customer-facing setup page (#1997), optionally ADDRESSED to one Phoenix site
   * (AB#2274): `/onboarding/{phoenixSiteId}` focuses that site's journey, which is what the
   * per-environment setup links deep-link to. Called without an id (or with an empty one) it stays the
   * overview route, so existing call sites keep working unchanged.
   */
  onboarding: (phoenixSiteId?: string): unknown[] =>
    phoenixSiteId ? ['/onboarding', phoenixSiteId] : ['/onboarding'],
  /** /backup-environments — the tenant's Backup Environments home (agent + bound-config cards). */
  backupEnvironments: (): unknown[] => ['/backup-environments'],
  /**
   * /backup-environments/{agentId} — the WORKSPACE ROOT of one Backup Environment. It carries no content of its
   * own: the shell redirects it to {@link APP_ROUTES.backupEnvironmentOverview}.
   */
  backupEnvironmentDetail: (agentId: string): unknown[] => ['/backup-environments', agentId],
  /** /backup-environments/{agentId}/{section} — the addressed section of the workspace. */
  backupEnvironmentSection: (agentId: string, section: EnvironmentSection): unknown[] => [
    '/backup-environments',
    agentId,
    section,
  ],
  /** /backup-environments/{agentId}/overview — the workspace's default section. */
  backupEnvironmentOverview: (agentId: string): unknown[] => ['/backup-environments', agentId, 'overview'],
  /** /backup-environments/{agentId}/export — export agent, plausibility and preflight (content: AB#2502). */
  backupEnvironmentExport: (agentId: string): unknown[] => ['/backup-environments', agentId, 'export'],
  /** /backup-environments/{agentId}/recovery — Recovery Environment + builder (content: AB#2501). */
  backupEnvironmentRecovery: (agentId: string): unknown[] => ['/backup-environments', agentId, 'recovery'],
  /** /backup-environments/{agentId}/configuration — the bound config's editor (content: AB#2500). */
  backupEnvironmentConfiguration: (agentId: string): unknown[] => [
    '/backup-environments',
    agentId,
    'configuration',
  ],
  /**
   * /backup-environments/{agentId}/configuration/observed — the configured-vs-observed drift view. It sits
   * UNDER the configuration section (it is a drill-in of that section, not a fifth tab), so the
   * Configuration tab stays the active one while it is open. The pre-W1a
   * `/backup-environments/{agentId}/observed` address redirects here.
   */
  backupEnvironmentObserved: (agentId: string): unknown[] => [
    '/backup-environments',
    agentId,
    'configuration',
    'observed',
  ],
  /** /settings — the tenant's company profile (CompanyAdmin only). */
  settings: (): unknown[] => ['/settings'],
  /** /subscription — the tenant's Stripe subscription overview + Customer-Portal hand-off (CompanyAdmin only). */
  subscription: (): unknown[] => ['/subscription'],
} as const;
